import { Component, ElementRef, HostListener, Input, OnDestroy } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ApiService } from '../../services/api.service';
import { UtilService } from '../../services/util.service';

interface QuickProduct {
  sku: string;
  productName: string;
  urlSlug: string;
}

interface QuickRow {
  /** What the customer typed or pasted in Part #. */
  text: string;
  qty: number;
  /** Set once the text is matched to an orderable product. */
  product: QuickProduct | null;
  suggestions: QuickProduct[];
  showSuggestions: boolean;
  error?: string;
}

const START_ROWS = 5;
const MORE_ROWS = 3;
const SUGGEST_SIZE = 8;
const RESOLVE_SIZE = 25;
const SUGGEST_DEBOUNCE_MS = 250;
const SUGGEST_MIN_WIDTH = 330;

/**
 * Header "Quick Order" dropdown: Part # / quantity rows sent to the cart in one go.
 * Part # suggestions and typed/pasted SKU resolution use POST /Products/quick-search
 * (it only returns products that can be added to cart); Add to Cart uses
 * POST /Cart/QuickAdd. Rejected rows stay with the backend's reason; added rows clear.
 */
@Component({
  selector: 'app-quick-order-menu',
  imports: [NgFor, NgIf, FormsModule, RouterLink],
  templateUrl: './quick-order-menu.component.html',
  styleUrl: './quick-order-menu.component.css',
})
export class QuickOrderMenuComponent implements OnDestroy {
  @Input() signedIn = false;

  open = false;
  rows: QuickRow[] = this.blankRows(START_ROWS);
  adding = false;
  notice: { kind: 'ok' | 'warn'; text: string } | null = null;
  /** Where the open suggestion list sits, relative to the panel. */
  suggestPos: { top: number; left: number; width: number } | null = null;
  private suggestAnchor: HTMLInputElement | null = null;

  private suggestTimers = new Map<QuickRow, ReturnType<typeof setTimeout>>();
  private suggestSeq = new Map<QuickRow, number>();

  constructor(private api: ApiService, private util: UtilService, private host: ElementRef<HTMLElement>) {}

  ngOnDestroy() {
    this.suggestTimers.forEach((t) => clearTimeout(t));
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.open && !this.host.nativeElement.contains(event.target as Node)) {
      this.open = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    this.open = false;
  }

  toggle() {
    this.open = !this.open;
  }

  close() {
    this.open = false;
  }

  get filledRows(): QuickRow[] {
    return this.rows.filter((row) => row.text.trim());
  }

  // ---------- rows ----------

  private blankRows(count: number): QuickRow[] {
    return Array.from({ length: count }, () => this.blankRow());
  }

  private blankRow(): QuickRow {
    return { text: '', qty: 1, product: null, suggestions: [], showSuggestions: false };
  }

  addRows() {
    this.rows = [...this.rows, ...this.blankRows(MORE_ROWS)];
  }

  removeRow(row: QuickRow) {
    this.rows = this.rows.filter((r) => r !== row);
    if (this.rows.length < START_ROWS) this.rows.push(this.blankRow());
  }

  stepQty(row: QuickRow, delta: number) {
    row.qty = this.cleanQty(row.qty + delta);
  }

  setQty(row: QuickRow, input: HTMLInputElement) {
    row.qty = this.cleanQty(input.value);
    row.error = undefined;
    input.value = String(row.qty);
  }

  private cleanQty(value: any): number {
    const n = Math.floor(Number(value));
    return Number.isFinite(n) && n > 0 ? n : 1;
  }

  // ---------- part # suggestions ----------

  onPartFocus(row: QuickRow, input: HTMLInputElement) {
    this.suggestAnchor = input;
    this.positionSuggestions();
    row.showSuggestions = !!row.suggestions.length && !row.product;
  }

  /**
   * The list is positioned against the panel, not the Part # box, so it floats over
   * the panel instead of being clipped inside the scrolling rows list.
   */
  positionSuggestions() {
    const input = this.suggestAnchor;
    const panel = input?.closest('.qo-panel') as HTMLElement | null;
    if (!input || !panel) return;
    const box = input.getBoundingClientRect();
    const frame = panel.getBoundingClientRect();
    this.suggestPos = {
      top: box.bottom - frame.top - panel.clientTop + 2,
      left: box.left - frame.left - panel.clientLeft,
      width: Math.max(box.width, SUGGEST_MIN_WIDTH),
    };
  }

  onPartInput(row: QuickRow, input: HTMLInputElement) {
    this.suggestAnchor = input;
    this.positionSuggestions();
    row.product = null;
    row.error = undefined;
    this.notice = null;
    const existing = this.suggestTimers.get(row);
    if (existing) clearTimeout(existing);
    if (!row.text.trim()) {
      row.suggestions = [];
      row.showSuggestions = false;
      return;
    }
    this.suggestTimers.set(row, setTimeout(() => this.loadSuggestions(row), SUGGEST_DEBOUNCE_MS));
  }

  private async loadSuggestions(row: QuickRow) {
    const seq = (this.suggestSeq.get(row) || 0) + 1;
    this.suggestSeq.set(row, seq);
    try {
      const list = await this.search(row.text.trim(), SUGGEST_SIZE);
      if (this.suggestSeq.get(row) !== seq || row.product) return;
      row.suggestions = list;
      row.showSuggestions = true;
    } catch (error) {
      console.log(error, 'quick order suggest error');
    }
  }

  chooseSuggestion(row: QuickRow, product: QuickProduct) {
    row.product = product;
    row.text = product.sku;
    row.error = undefined;
    row.suggestions = [];
    row.showSuggestions = false;
  }

  /** Delay so a click on a suggestion lands before the list goes away. */
  onPartBlur(row: QuickRow) {
    setTimeout(() => (row.showSuggestions = false), 150);
  }

  /** A pasted multi-line list (one "SKU qty" per line) fills rows from this one down. */
  onPartPaste(row: QuickRow, event: ClipboardEvent) {
    const text = event.clipboardData?.getData('text') || '';
    const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (lines.length < 2) return;
    event.preventDefault();

    let index = this.rows.indexOf(row);
    for (const line of lines) {
      const [sku, qty] = line.split(/[\t,; ]+/);
      if (index >= this.rows.length) this.rows.push(this.blankRow());
      const target = this.rows[index++];
      target.text = sku;
      target.qty = this.cleanQty(qty ?? 1);
      target.product = null;
      target.error = undefined;
    }
    this.rows = [...this.rows];
  }

  private async search(text: string, pageSize: number): Promise<QuickProduct[]> {
    const res = await this.api.quickOrderSearch(text, 1, pageSize);
    return (res?.products || []).map((p: any) => ({
      sku: p.sku || '',
      productName: p.productName || '',
      urlSlug: p.urlSlug,
    }));
  }

  /** Typed/pasted rows nobody picked a suggestion for: exact SKU match, else the only hit. */
  private async resolveRows(rows: QuickRow[]) {
    await Promise.all(
      rows
        .filter((row) => !row.product)
        .map(async (row) => {
          try {
            const text = row.text.trim();
            // Wide page: partial-SKU matching ranks "ABC-25" next to "ABC", so the
            // exact SKU is not guaranteed to be in the first few hits.
            const hits = await this.search(text, RESOLVE_SIZE);
            const exact = hits.find((p) => p.sku.toLowerCase() === text.toLowerCase());
            row.product = exact || (hits.length === 1 ? hits[0] : null);
            if (!row.product) {
              row.error = hits.length ? 'More than one match — pick one from the list.' : 'No product found for this part #.';
            }
          } catch {
            row.error = 'Could not look up this part #.';
          }
        })
    );
  }

  // ---------- add to cart ----------

  async addToCart() {
    const filled = this.filledRows;
    if (!filled.length || this.adding) return;
    this.adding = true;
    this.notice = null;
    filled.forEach((row) => (row.error = undefined));
    try {
      await this.resolveRows(filled);
      const ready = filled.filter((row) => row.product);
      if (!ready.length) {
        this.notice = { kind: 'warn', text: 'Nothing was added — check the part numbers below.' };
        return;
      }

      const res = await this.api.quickOrderAdd(
        ready.map((row) => ({ ProductSlug: row.product!.urlSlug, Qty: row.qty }))
      );
      const added = this.applyAddResult(res, ready);
      await this.refreshCart();

      const problems = this.rows.filter((row) => row.error).length;
      this.notice = problems
        ? { kind: 'warn', text: `${added} added to cart. ${problems} need${problems === 1 ? 's' : ''} attention below.` }
        : { kind: 'ok', text: `${added} ${added === 1 ? 'item' : 'items'} added to your cart.` };
    } catch (error: any) {
      console.log(error, 'quick order add error');
      this.notice = {
        kind: 'warn',
        text: error?.response?.data?.errors?.[0] || error?.response?.data?.warnings?.[0] || 'Something went wrong. Please try again.',
      };
    } finally {
      this.adding = false;
    }
  }

  /** Clears the rows that went in, flags the rest. Returns how many were added. */
  private applyAddResult(res: any, sent: QuickRow[]): number {
    // Per-line outcome: { productSlug, added, reason }.
    const failures = new Map<string, string>();
    for (const r of res?.results || []) {
      if (!r.added) failures.set(r.productSlug, r.reason || 'Could not be added.');
    }

    let added = 0;
    const keep: QuickRow[] = [];
    for (const row of this.rows) {
      if (!sent.includes(row)) {
        if (row.text.trim()) keep.push(row);
        continue;
      }
      const reason = failures.get(row.product!.urlSlug);
      if (reason) {
        row.error = reason;
        keep.push(row);
      } else {
        added++;
      }
    }
    this.rows = [...keep, ...this.blankRows(Math.max(START_ROWS - keep.length, 1))];
    return res?.addedCount ?? added;
  }

  private async refreshCart() {
    try {
      const cartList = await this.api.getCartList();
      if (cartList) this.util.CartUpdated(cartList);
    } catch (error) {
      console.log(error, 'cart refresh error');
    }
  }
}
